import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight } from "lucide-react";
import api, { API_BASE } from "../lib/api";

const reveal = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
};

export default function NocturneWelcome() {
  const [params] = useSearchParams();
  const firstName = params.get("fn") || "";
  const [pdf, setPdf] = useState(null);
  const nav = useNavigate();
  const btnRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const unlocked = sessionStorage.getItem("gb_nocturne_unlocked");
    if (!unlocked) { nav("/nocturne"); return; }
    api.get("/magazine/latest").then((r) => setPdf(r.data)).catch(() => setPdf({ available: false }));
  }, [nav]);

  useEffect(() => {
    if (pdf?.available && btnRef.current) {
      const t = setTimeout(() => {
        btnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setPulse(true);
        setTimeout(() => setPulse(false), 2400);
      }, 900);
      return () => clearTimeout(t);
    }
  }, [pdf]);

  const downloadHref = `${API_BASE}/magazine/download`;

  return (
    <main data-testid="nocturne-welcome-page" className="bg-black min-h-screen pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.p {...reveal} className="overline mb-6">
          Nocturne · Issue 01 · The Art of Control
        </motion.p>
        <motion.h1
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="font-display text-6xl md:text-9xl text-white leading-[0.9]"
          data-testid="welcome-headline"
        >
          Welcome to
          <br />
          <span className="italic text-[var(--gb-gold)]">Nocturne.</span>
        </motion.h1>

        <motion.p
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.25 }}
          className="text-white/70 text-xl md:text-2xl max-w-2xl mt-12 leading-relaxed font-display italic"
          data-testid="welcome-body"
        >
          {firstName ? `${firstName}, y` : "Y"}our copy of Issue 01 is ready.
        </motion.p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.35 }}
          className="md:col-span-6"
        >
          <div className="aspect-[4/5] w-full max-w-md relative overflow-hidden noise">
            <img
              src="https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=1200&q=90"
              alt="Nocturne Issue 01 cover"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6">
              <p className="kbd text-white">NOCTURNE · ISSUE 01</p>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-display text-2xl text-white italic">The Art of Control</p>
              <div className="gb-divider my-3" />
              <p className="kbd text-white/60">A GOOD BANDIT PUBLICATION</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.45 }}
          className="md:col-span-5 md:col-start-8"
        >
          <p className="overline mb-4">Your Download</p>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">
            Download the
            <br />
            <span className="italic text-[var(--gb-gold)]">inaugural issue.</span>
          </h2>
          <p className="text-white/60 leading-relaxed mt-6 max-w-md">
            Cinema, photography and publishing in a single collectible edition. A copy
            has also been sent to your inbox.
          </p>

          {pdf?.available ? (
            <a
              ref={btnRef}
              data-testid="welcome-download-button"
              href={downloadHref}
              target="_blank"
              rel="noopener noreferrer"
              download
              className={`gb-btn-primary mt-10 ${pulse ? "gb-pulse" : ""}`}
            >
              <Download size={14} /> Download the Issue
            </a>
          ) : (
            <div className="mt-10">
              <div className="border border-white/10 p-6 max-w-md">
                <p className="kbd text-[var(--gb-gold)] mb-2">PREPARING</p>
                <p className="text-white text-sm leading-relaxed">
                  Issue 01 is being finalized. We'll email you the moment it's live at
                  the address you provided.
                </p>
              </div>
            </div>
          )}

          <div className="gb-divider my-10" />
          <p className="kbd text-white/40 mb-4">WHAT'S INSIDE</p>
          <ul className="space-y-2 text-white/70 text-sm">
            <li>Photography</li>
            <li>Journal</li>
            <li>Behind the Drive</li>
            <li>Dealership Insider</li>
          </ul>

          <Link
            to="/nocturne"
            className="inline-flex items-center gap-2 text-[var(--gb-gold)] text-xs uppercase tracking-[0.25em] mt-10 gb-link"
          >
            Return to Nocturne <ArrowRight size={12} />
          </Link>
        </motion.div>
      </section>
    </main>
  );
}
